import cx from 'classnames';
import { Link } from 'gatsby';
import { gsap } from 'gsap';
import parse from 'html-react-parser';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/ui';
import { usePageContext } from '../../../providers/PageContext';
import { media } from '../../../styles/media';
import image1 from '../../assets/image--blog-1.jpg';
import image2 from '../../assets/image--blog-2.jpg';
import { blogPath, blogArticlePath } from '../utils/routes';

const Section = styled.section`
  opacity: 0;
  .blog--card--bg-1 {
    background-image: url(${image1});
  }
  .blog--card--bg-2 {
    background-image: url(${image2});
  }
  .line--clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .blog--card {
    transition: all 0.3s linear;
    svg {
      transition: all 0.3s linear;
    }
    &:hover,
    &:focus {
      filter: drop-shadow(10px 20px 34px rgba(0, 0, 0, 0.05));
      .blog--card--bg {
        transform: scale(1.025);
      }
      svg {
        margin-inline-start: 10px;
      }
    }
  }
  .blog--card--image {
    width: 100%;
    height: 170px;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    @media ${media.lgUp} {
      width: 230px;
      height: auto;
    }
  }

  .blog--card--bg {
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    transition: all 0.3s linear;
  }
`;

export default function BlockBlogs({ posts, previousPagePath, nextPagePath }) {
  const { siteTag, siteLang } = usePageContext();
  const siteLangCode = siteLang.split('-')[0];
  const blogLang =
    siteTag === 'site:citychefs' && ['pt', 'it'].includes(siteLangCode)
      ? siteLangCode
      : siteTag === 'site:kitchenpark' && ['ar', 'en'].includes(siteLangCode)
        ? siteLangCode
        : undefined;

  useEffect(() => {
    const fadeElements = gsap.timeline();

    fadeElements.to('#block-blogs', {
      opacity: 1,
      delay: 0.25,
    });

    const fadeUp = document.querySelectorAll('#block-blogs .fade-up');

    fadeUp.forEach(function (element) {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 60%',
        },
        duration: 0.5,
        opacity: '0',
        y: '20%',
      });
    });
  }, []);

  return (
    <Section id="block-blogs" className="bg--grey-1">
      <div className="container pb-20 pt-3 md:pb-24 lg:pb-40">
        <div className="grid grid-cols-4 gap-x-10 gap-y-12 md:grid-cols-12">
          {posts
            .filter((post) => {
              if (
                siteTag !== 'site:citychefs' &&
                siteTag !== 'site:kitchenpark'
              ) {
                return true;
              }

              if (siteTag === 'site:citychefs') {
                if (siteLangCode === 'it') {
                  return post.uri.includes('/it/');
                }

                if (siteLangCode === 'pt') {
                  return !post.uri.includes('/it/');
                }
              }

              if (siteTag === 'site:kitchenpark') {
                if (siteLangCode === 'ar') {
                  return post.uri.includes('/ar/');
                }

                if (siteLangCode === 'en') {
                  return !post.uri.includes('/ar/');
                }
              }

              return true;
            })
            .map((post) => {
              const { title } = post;
              const featuredImage = {
                src: post.featuredImage?.node?.localFile?.childImageSharp?.fluid
                  ?.src,
                alt: post.featuredImage?.node?.alt || '',
              };
              return (
                <article
                  itemScope
                  itemType="http://schema.org/Article"
                  className="col-span-4 md:col-span-6"
                  key={post.uri}>
                  <Link
                    to={
                      siteTag === 'site:foodstars'
                        ? blogArticlePath(siteTag, post.uri)
                        : siteTag === 'site:citychefs' ||
                            siteTag === 'site:kitchenpark'
                          ? blogPath(siteTag, post.uri, blogLang)
                          : blogPath(siteTag, post.uri)
                    }
                    itemProp="url"
                    className="blog--card bg--white fade-up block lg:flex lg:pt-6">
                    <div className="blog--card--image flex-none">
                      <div
                        className="blog--card--bg"
                        style={{
                          backgroundImage: `url(${featuredImage.src})`,
                        }}
                      />
                    </div>

                    <div className="p-6 md:pl-12 md:pr-12 md:pt-8 lg:pb-6 lg:pl-8 lg:pr-14 lg:pt-0">
                      <div className="eyebrow color--grey-4 mb-4">
                        {post.date}
                      </div>

                      <p className="h5 line--clamp mb-6" itemProp="headline">
                        {parse(title)}
                      </p>

                      <span className="btn--text--svg color--main">
                        <span>{/* Learn More */}</span>

                        <svg
                          width="8"
                          height="8"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path d="M.305.348l6.81.013.012 6.81H5.83l.05-4.608-4.848 4.85-.968-.968 4.85-4.85-4.608.052V.348z" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </article>
              );
            })}

          <div className="col-span-4 md:col-span-12">
            <div
              className={cx(
                'flex w-full',
                previousPagePath ? 'justify-between' : 'justify-end'
              )}>
              {previousPagePath && (
                <>
                  <Button href={previousPagePath}>
                    {global?.blog_previous_button || 'Back'}
                  </Button>

                  <br />
                </>
              )}
              {nextPagePath && (
                <Button href={nextPagePath}>
                  {global?.blog_next_button || 'Next'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
